import { useCallback, useMemo, useState } from "react";
import { useForm, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FunnelPlus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/popover";
import { Badge } from "../../../ui/badge";
import { Label } from "../../../ui/label";
import Button from "../../Button";
import Form from "../../Form";
import Item from "./Item";
import { useFilters } from "../../../../utils/hooks/useFilters";
import { findFilterConfig, getDefaultFilterValue, getFieldMethod, type FieldType } from "./helpers";
import type { FilterConfig, FilterRow, FiltersFormValues } from "./types";

const EMPTY_ROW = { name: "", method: "", value: "" };

type FiltersProps = {
    config?: FilterConfig[];
    defaultFilter?: string;
};

function Filters({ defaultFilter, config = [] }: FiltersProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [{ filters = [] }, setFilters] = useFilters();

    const getInitialFilters = useCallback((): FilterRow[] => {
        if (filters.length > 0) return filters;
        if (!defaultFilter) return [EMPTY_ROW];

        const fieldType = findFilterConfig(config, defaultFilter)?.type as FieldType | undefined;
        return [{
            name: defaultFilter,
            method: getFieldMethod(fieldType),
            value: getDefaultFilterValue(fieldType),
        }];
    }, [filters, defaultFilter, config]);

    const form = useForm<FiltersFormValues>({
        defaultValues: useMemo(() => ({ filters: getInitialFilters() }), [getInitialFilters]),
    });

    const onOpenChange = useCallback((next: boolean) => {
        setOpen(next);
        if (next) {
            form.reset({ filters: getInitialFilters() });
        }
    }, [form, getInitialFilters]);

    const onClear = useCallback(() => {
        form.reset({ filters: [EMPTY_ROW] });
        setFilters("filters", []);
        setOpen(false);
    }, [form, setFilters]);

    const onSearch = form.handleSubmit((values) => {
        setFilters("filters", values.filters);
        setOpen(false);
    });

    const { isDirty } = useFormState({ control: form.control });
    const filtersValue = useWatch({ control: form.control, name: "filters" });

    const hasValidFilters = useMemo(() => (filtersValue || []).some((row) => {
        if (!row?.name) return false;
        const value = row.value;
        if (value === undefined || value === null || value === "") return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
    }), [filtersValue]);

    const searchDisabled = !isDirty || !hasValidFilters;

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverTrigger asChild>
                <Button variant="outline">
                    <FunnelPlus size={18} />
                    {t("filters")}
                    {filters.length > 0 && (
                        <Badge
                            className="tw-rounded tw-text-[0.625rem] tw-w-4.5 tw-h-4.5 tw-flex tw-items-center tw-justify-center"
                            variant="secondary"
                        >
                            {filters.length}
                        </Badge>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="tw-w-[420px] tw-max-w-[90vw] tw-shadow-sm">
                <Form form={form}>
                    <Label>{t("filters")}</Label>
                    <Form.List name="filters">
                        {(fields, { insert, remove }) => (
                            <div className="tw-flex tw-flex-col tw-gap-3">
                                {fields.map((item, index) => (
                                    <Item
                                        key={index}
                                        item={item}
                                        index={index}
                                        insert={insert}
                                        remove={remove}
                                        config={config}
                                    />
                                ))}
                            </div>
                        )}
                    </Form.List>

                    <div className="tw-flex tw-gap-2 tw-justify-end tw-mt-4 tw-border-t tw-pt-3">
                        <Button type="button" variant="outline" onClick={onClear}>
                            {t("clear")}
                        </Button>
                        <Button type="button" onClick={onSearch} disabled={searchDisabled}>
                            {t("search")}
                        </Button>
                    </div>
                </Form>
            </PopoverContent>
        </Popover>
    );
}

export default Filters;
